import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, User, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PostDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [popularPosts, setPopularPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentForm, setCommentForm] = useState({ author_name: '', author_email: '', content: '' });
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  useEffect(() => {
    fetchData();
  }, [slug]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const postRes = await axios.get(`${API}/posts/${slug}`);
      setPost(postRes.data);

      const [relatedRes, commentsRes, popularRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/posts/${postRes.data.id}/related?limit=3`),
        axios.get(`${API}/posts/${postRes.data.id}/comments`),
        axios.get(`${API}/posts/popular?limit=5`),
        axios.get(`${API}/categories`),
      ]);

      setRelatedPosts(relatedRes.data);
      setComments(commentsRes.data);
      setPopularPosts(popularRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingComment(true);

    try {
      const response = await axios.post(`${API}/comments`, {
        ...commentForm,
        post_id: post.id,
      });
      setComments([response.data, ...comments]);
      setCommentForm({ author_name: '', author_email: '', content: '' });
      toast.success('Comment posted successfully!');
    } catch (error) {
      toast.error('Failed to post comment. Please try again.');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-bold" data-testid="loading-indicator">Loading...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-bold" data-testid="post-not-found">Post not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Image */}
      <div className="relative h-[500px] mb-12" data-testid="post-hero-image">
        <img
          src={post.featured_image}
          alt={post.title}
          className="w-full h-full object-cover"
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
          <article className="lg:col-span-2" data-testid="post-content">
            {/* Post Header */}
            <div className="mb-8">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-primary" data-testid="post-category">
                {post.category}
              </span>
              <h1 className="text-5xl sm:text-6xl tracking-tight leading-none font-black mt-4" data-testid="post-title">
                {post.title}
              </h1>
              <div className="flex items-center space-x-6 mt-6 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <img
                    src={post.author_image}
                    alt={post.author}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <span className="font-medium" data-testid="post-author">{post.author}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4" />
                  <span data-testid="post-reading-time">{post.reading_time} min read</span>
                </div>
                <span data-testid="post-views">{post.views} views</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center space-x-4 mb-8 pb-8 border-b border-border" data-testid="share-buttons">
              <span className="text-sm font-bold">Share:</span>
              <button className="p-2 hover:bg-secondary transition-colors" data-testid="share-facebook">
                <Facebook className="w-5 h-5" />
              </button>
              <button className="p-2 hover:bg-secondary transition-colors" data-testid="share-twitter">
                <Twitter className="w-5 h-5" />
              </button>
              <button className="p-2 hover:bg-secondary transition-colors" data-testid="share-linkedin">
                <Linkedin className="w-5 h-5" />
              </button>
            </div>

            {/* Post Content */}
            <div 
              className="prose prose-lg max-w-none mb-16" 
              data-testid="post-body"
              dangerouslySetInnerHTML={{ __html: post.content }}
              style={{
                fontSize: '1.125rem',
                lineHeight: '1.75rem'
              }}
            />

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <div className="border-t border-border pt-12 mb-16" data-testid="related-posts-section">
                <h2 className="text-3xl sm:text-4xl tracking-tight leading-tight font-bold mb-8">
                  Related Articles
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                  {relatedPosts.map((relatedPost) => (
                    <Link
                      key={relatedPost.id}
                      to={`/post/${relatedPost.slug}`}
                      className="block group"
                      data-testid={`related-post-${relatedPost.slug}`}
                    >
                      <img
                        src={relatedPost.featured_image}
                        alt={relatedPost.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <h3 className="text-xl font-bold mt-4 group-hover:text-primary transition-colors">
                        {relatedPost.title}
                      </h3>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Comments Section */}
            <div className="border-t border-border pt-12" data-testid="comments-section">
              <h2 className="text-3xl sm:text-4xl tracking-tight leading-tight font-bold mb-8">
                Comments ({comments.length})
              </h2>

              {/* Comment Form */}
              <form onSubmit={handleCommentSubmit} className="mb-12" data-testid="comment-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <input
                    type="text"
                    placeholder="Your name"
                    value={commentForm.author_name}
                    onChange={(e) => setCommentForm({ ...commentForm, author_name: e.target.value })}
                    required
                    className="px-4 py-3 border border-border focus:outline-none focus:ring-2 focus:ring-ring"
                    data-testid="comment-name-input"
                  />
                  <input
                    type="email"
                    placeholder="Your email"
                    value={commentForm.author_email}
                    onChange={(e) => setCommentForm({ ...commentForm, author_email: e.target.value })}
                    required
                    className="px-4 py-3 border border-border focus:outline-none focus:ring-2 focus:ring-ring"
                    data-testid="comment-email-input"
                  />
                </div>
                <textarea
                  placeholder="Your comment"
                  value={commentForm.content}
                  onChange={(e) => setCommentForm({ ...commentForm, content: e.target.value })}
                  required
                  rows={4}
                  className="w-full px-4 py-3 border border-border focus:outline-none focus:ring-2 focus:ring-ring mb-4"
                  data-testid="comment-content-input"
                />
                <button
                  type="submit"
                  disabled={isSubmittingComment}
                  className="px-6 py-3 bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors duration-300 disabled:opacity-50"
                  data-testid="comment-submit-btn"
                >
                  {isSubmittingComment ? 'Posting...' : 'Post Comment'}
                </button>
              </form>

              {/* Comments List */}
              <div className="space-y-6" data-testid="comments-list">
                {comments.map((comment) => (
                  <div key={comment.id} className="border-b border-border pb-6" data-testid={`comment-${comment.id}`}>
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                        {comment.author_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-bold">{comment.author_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(comment.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <p className="text-base leading-relaxed ml-13">{comment.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </article>

          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Sidebar popularPosts={popularPosts} categories={categories} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostDetail;
